import React, { useState } from 'react';
import { Layout } from '../components/layout/Layout';
import { SectionHeading } from '../components/ui/SectionHeading';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, BookOpen, Heart, Target, Eye, Compass, Star, Shield, CheckCircle2, Quote } from 'lucide-react';
interface FaithStatement {
  id: number;
  title: string;
  content: string;
  scripture?: string;
}
const faithStatements: FaithStatement[] = [{
  id: 1,
  title: 'The Scripture',
  content: 'We believe the Bible to be the inspired, the only infallible, authoritative Word of God. When rightly divided and properly interpreted, it provides sufficient revelation for the church to be complete in doctrine, maturity, ministry and worship.',
  scripture: 'II Timothy 3:16; I Thess. 2:13; II Peter 1:21'
}, {
  id: 2,
  title: 'The Godhead',
  content: 'We believe that there is one God, eternally existent in three persons: Father, Son, and Holy Spirit.',
  scripture: 'Deut. 6:4; Isaiah 43:10; Matt. 28:19; Luke 3:22'
}, {
  id: 3,
  title: 'The Deity of Christ',
  content: 'We believe in the deity of our Lord Jesus Christ, in His virgin birth, in His sinless life, in His miracles, in His vicarious and atoning death through His shed blood, in His bodily resurrection, in His ascension to the right hand of the Father, and in His personal return in power and glory.',
  scripture: 'John 1:1-4; Matt. 1:23; Heb. 7:26; I Cor. 15:3; Acts 1:9'
}, {
  id: 4,
  title: 'Salvation',
  content: 'We believe that for the salvation of lost and sinful man, regeneration by the Holy Spirit is absolutely essential.',
  scripture: 'John 3:3; Rom. 10:13-15; Eph. 2:8'
}, {
  id: 5,
  title: 'The Holy Spirit',
  content: 'We believe in the present ministry of the Holy Spirit by whose indwelling the Christian is enabled to live a godly life. We believe in the baptism of the Holy Spirit with the evidence of speaking in other tongues as the Spirit gives utterance.',
  scripture: 'Acts 2:4; Rom. 8:16; I Cor. 12:1-31'
}, {
  id: 6,
  title: 'Resurrection',
  content: 'We believe in the resurrection of both the saved and the lost; they that are saved unto the resurrection of life and they that are lost unto the resurrection of damnation.',
  scripture: 'John 5:28-29; Rev. 20:11-15'
}, {
  id: 7,
  title: 'Spiritual Unity',
  content: 'We believe in the spiritual unity of believers in our Lord Jesus Christ.',
  scripture: 'Eph. 4:1-6; Phil. 2:1-4'
}, {
  id: 8,
  title: 'Divine Healing',
  content: 'We believe that divine healing is an integral part of the gospel. Deliverance from sickness is provided for in the atonement, and is the privilege of all believers.',
  scripture: 'Isaiah 53:4-5; Matt. 8:16-17; James 5:14-16'
}, {
  id: 9,
  title: 'The Blessed Hope',
  content: "We believe in the blessed hope—the rapture of the Church at Christ's coming.",
  scripture: 'Titus 2:13; I Thess. 4:16-17; Rom. 8:23'
}, {
  id: 10,
  title: 'The Church',
  content: 'We believe that the Church is the body of Christ, the habitation of God through the Spirit, with divine appointments for the fulfillment of her great commission.',
  scripture: 'Eph. 1:22-23; Eph. 2:22; Heb. 12:23'
}, {
  id: 11,
  title: 'Ordinances',
  content: "We believe in the ordinances of the church: Water Baptism by immersion and Holy Communion (The Lord's Supper).",
  scripture: 'Matt. 28:19; Rom. 6:4; I Cor. 11:26'
}, {
  id: 12,
  title: 'Sanctification',
  content: 'We believe in sanctification—an act of separation from that which is evil, and of dedication unto God.',
  scripture: 'Rom. 12:1-2; I Thess. 5:23; Heb. 13:12'
}];
const coreValues = [{
  letter: 'R',
  word: 'Respect',
  description: 'We value every person as a unique creation of God, treating all with dignity and honor.',
  scripture: 'Phil. 2:3'
}, {
  letter: 'E',
  word: 'Excellence',
  description: 'We give our best to God in everything we do, reflecting His glory through our standards.',
  scripture: 'Col. 3:23'
}, {
  letter: 'L',
  word: 'Love',
  description: 'The foundation of our faith—loving God and loving people unconditionally.',
  scripture: 'John 13:34'
}, {
  letter: 'A',
  word: 'Accountability',
  description: 'We take responsibility for our growth and actions before God and one another.',
  scripture: 'Matt. 25:14-33'
}, {
  letter: 'T',
  word: 'Transparency',
  description: 'We walk in the light, being honest and open in our relationships and ministry.',
  scripture: 'Rom. 12:10'
}, {
  letter: 'E',
  word: 'Empowerment',
  description: 'We equip and release people to fulfill their God-given potential and purpose.',
  scripture: 'Deut. 8:18'
}];
const purposes = [{
  title: 'Evangelism',
  icon: <Target className="w-6 h-6" data-id="element-1415" />
}, {
  title: 'Worship',
  icon: <Star className="w-6 h-6" data-id="element-1416" />
}, {
  title: 'Fellowship',
  icon: <Heart className="w-6 h-6" data-id="element-1417" />
}, {
  title: 'Ministry',
  icon: <Shield className="w-6 h-6" data-id="element-1418" />
}, {
  title: 'Discipleship',
  icon: <BookOpen className="w-6 h-6" data-id="element-1419" />
}];
export function WhatWeBelievePage() {
  const [expandedId, setExpandedId] = useState<number | null>(1);
  return <Layout data-id="element-1420">
      {/* Hero Section */}
      <section className="relative pt-44 md:pt-56 pb-24 md:pb-32 bg-[#0A1F44] overflow-hidden" data-id="element-1421">
        <div className="absolute inset-0 z-0" data-id="element-1422">
          <img src="https://images.unsplash.com/photo-1504052434569-70ad5836ab65?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80" alt="Bible and Light" className="w-full h-full object-cover opacity-20" data-id="element-1423" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A1F44]/90 via-[#0A1F44]/80 to-[#0A1F44]" data-id="element-1424" />
        </div>

        <div className="container-custom relative z-10" data-id="element-1425">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} className="max-w-4xl mx-auto text-center" data-id="element-1426">
            <div className="inline-flex items-center gap-3 mb-6" data-id="element-1427">
              <div className="h-[1px] w-8 md:w-12 bg-[#fc0719]" data-id="element-1428" />
              <span className="text-[#fc0719] text-sm md:text-base font-bold tracking-[0.4em] uppercase" data-id="element-1429">
                Our Foundations
              </span>
              <div className="h-[1px] w-8 md:w-12 bg-[#fc0719]" data-id="element-1430" />
            </div>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-serif font-bold text-white mb-8 leading-tight" data-id="element-1431">
              What We <span className="text-[#fc0719]" data-id="element-1432">Believe</span>
            </h1>
            <p className="text-xl md:text-2xl text-white/80 leading-relaxed max-w-2xl mx-auto font-medium" data-id="element-1433">
              Rooted in Scripture, empowered by the Spirit, and committed to the
              Great Commission.
            </p>
          </motion.div>
        </div>

        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#fc0719]/10 rounded-full blur-[120px] -mr-48 -mt-48" data-id="element-1434" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-500/10 rounded-full blur-[120px] -ml-48 -mb-48" data-id="element-1435" />
      </section>

      {/* Vision & Mission Section */}
      <section className="py-24 bg-white relative" data-id="element-1436">
        <div className="container-custom" data-id="element-1437">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-stretch" data-id="element-1438">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="bg-[#0A1F44] rounded-3xl p-10 md:p-14 text-white flex flex-col relative overflow-hidden shadow-2xl" data-id="element-1439">
              <div className="absolute top-0 right-0 w-40 h-40 bg-white/5 rounded-full -mr-20 -mt-20" data-id="element-1440" />
              <div className="w-16 h-16 bg-[#fc0719] rounded-2xl flex items-center justify-center mb-8 shadow-lg shadow-red-900/20" data-id="element-1441">
                <Eye className="w-8 h-8 text-white" data-id="element-1442" />
              </div>
              <h2 className="text-3xl md:text-4xl font-serif font-bold mb-6" data-id="element-1443">
                Our Vision
              </h2>
              <p className="text-xl text-white/80 leading-relaxed font-medium" data-id="element-1444">
                "To develop leaders and world changers within the family of God,
                empowered with roots for growth and wings for flight."
              </p>
              <div className="mt-auto pt-10 flex items-center gap-4" data-id="element-1445">
                <div className="h-[1px] w-12 bg-[#fc0719]" data-id="element-1446" />
                <span className="text-[#fc0719] font-bold tracking-widest text-sm uppercase" data-id="element-1447">
                  The Goal
                </span>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="bg-[#fc0719] rounded-3xl p-10 md:p-14 text-white flex flex-col relative overflow-hidden shadow-2xl" data-id="element-1448">
              <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -mr-20 -mt-20" data-id="element-1449" />
              <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mb-8 shadow-lg shadow-red-900/20" data-id="element-1450">
                <Target className="w-8 h-8 text-[#fc0719]" data-id="element-1451" />
              </div>
              <h2 className="text-3xl md:text-4xl font-serif font-bold mb-6" data-id="element-1452">
                Our Mission
              </h2>
              <p className="text-xl text-white/90 leading-relaxed font-medium" data-id="element-1453">
                "To enlighten, engage, equip, enrich and empower individuals; to
                discover and maximize their God-given potential."
              </p>
              <div className="mt-auto pt-10 flex items-center gap-4" data-id="element-1454">
                <div className="h-[1px] w-12 bg-white/40" data-id="element-1455" />
                <span className="text-white/80 font-bold tracking-widest text-sm uppercase" data-id="element-1456">
                  The Strategy
                </span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Values (RELATE) Section */}
      <section className="py-24 bg-[#F5F7FA] relative overflow-hidden" data-id="element-1457">
        <div className="container-custom relative z-10" data-id="element-1458">
          <SectionHeading title="Our Core Values" subtitle="The Heart of Our Culture" data-id="element-1459" />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-16" data-id="element-1460">
            {coreValues.map((value, index) => <motion.div key={value.letter} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 hover:shadow-xl transition-all duration-300 group" data-id="element-1461">
                <div className="flex items-start justify-between mb-6" data-id="element-1462">
                  <div className="text-6xl font-serif font-black text-[#fc0719]/10 group-hover:text-[#fc0719]/20 transition-colors" data-id="element-1463">
                    {value.letter}
                  </div>
                  <div className="text-[#0A1F44] font-bold text-xs tracking-widest uppercase bg-[#F5F7FA] px-3 py-1 rounded-full" data-id="element-1464">
                    {value.scripture}
                  </div>
                </div>
                <h3 className="text-2xl font-serif font-bold text-[#0A1F44] mb-4 group-hover:text-[#fc0719] transition-colors" data-id="element-1465">
                  {value.word}
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-1466">
                  {value.description}
                </p>
              </motion.div>)}
          </div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="mt-16 max-w-4xl mx-auto bg-[#0A1F44] rounded-2xl p-8 md:p-12 text-center relative overflow-hidden" data-id="element-1467">
            <Quote className="absolute top-6 left-6 w-12 h-12 text-white/5" data-id="element-1468" />
            <h3 className="text-2xl md:text-3xl font-serif font-bold text-white mb-6 italic" data-id="element-1469">
              "We RELATE to God and to one another with honor and integrity."
            </h3>
            <div className="flex items-center justify-center gap-2 text-[#fc0719] font-bold tracking-widest uppercase text-sm" data-id="element-1470">
              <div className="h-[1px] w-8 bg-[#fc0719]" data-id="element-1471" />
              Our Culture Code
              <div className="h-[1px] w-8 bg-[#fc0719]" data-id="element-1472" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* 5-Fold Purpose Section */}
      <section className="py-24 bg-white" data-id="element-1473">
        <div className="container-custom" data-id="element-1474">
          <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-1475">
            <Compass className="w-12 h-12 text-[#fc0719] mx-auto mb-6" data-id="element-1476" />
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#0A1F44] mb-6 uppercase tracking-wide" data-id="element-1477">
              Our 5-Fold Purpose
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed" data-id="element-1478">
              In accordance with the Great Commission (Matt. 28:18), we are
              called to engage, enrich, enlighten, equip and empower people
              through these five pillars.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-6" data-id="element-1479">
            {purposes.map((purpose, index) => <motion.div key={purpose.title} initial={{
            opacity: 0,
            scale: 0.9
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="bg-[#F5F7FA] p-8 rounded-2xl text-center hover:bg-[#0A1F44] hover:text-white transition-all duration-300 group cursor-default" data-id="element-1480">
                <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center mx-auto mb-6 shadow-sm text-[#fc0719] group-hover:bg-[#fc0719] group-hover:text-white transition-colors" data-id="element-1481">
                  {purpose.icon}
                </div>
                <div className="text-xs font-bold text-gray-400 mb-1 uppercase tracking-widest group-hover:text-white/60" data-id="element-1482">
                  Pillar 0{index + 1}
                </div>
                <h4 className="text-lg font-bold text-[#0A1F44] group-hover:text-white uppercase tracking-wider" data-id="element-1483">
                  {purpose.title}
                </h4>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Detailed Statement of Faith Section */}
      <section className="py-24 bg-[#0A1F44] relative overflow-hidden" data-id="element-1484">
        {/* Subtle background texture */}
        <div className="absolute inset-0 opacity-5 pointer-events-none" data-id="element-1485">
          <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]" data-id="element-1486" />
        </div>

        <div className="container-custom relative z-10" data-id="element-1487">
          <SectionHeading title="Statement of Faith" subtitle="The Core of Our Doctrine" light data-id="element-1488" />

          <div className="max-w-4xl mx-auto space-y-4 mt-16" data-id="element-1489">
            {faithStatements.map(statement => <motion.div key={statement.id} initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className={`rounded-2xl overflow-hidden transition-all duration-300 border ${expandedId === statement.id ? 'bg-white border-white shadow-2xl' : 'bg-white/5 border-white/10 hover:bg-white/10'}`} data-id="element-1490">
                <button onClick={() => setExpandedId(expandedId === statement.id ? null : statement.id)} className="w-full px-8 py-6 flex items-center justify-between text-left transition-colors" data-id="element-1491">
                  <div className="flex items-center gap-6" data-id="element-1492">
                    <span className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold shrink-0 transition-colors ${expandedId === statement.id ? 'bg-[#fc0719] text-white' : 'bg-white/10 text-white/60'}`} data-id="element-1493">
                      {statement.id < 10 ? `0${statement.id}` : statement.id}
                    </span>
                    <h3 className={`font-serif font-bold text-xl transition-colors ${expandedId === statement.id ? 'text-[#0A1F44]' : 'text-white'}`} data-id="element-1494">
                      {statement.title}
                    </h3>
                  </div>
                  <ChevronDown className={`w-6 h-6 transition-transform duration-300 ${expandedId === statement.id ? 'rotate-180 text-[#fc0719]' : 'text-white/40'}`} data-id="element-1495" />
                </button>

                <AnimatePresence data-id="element-1496">
                  {expandedId === statement.id && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3
              }} data-id="element-1497">
                      <div className="px-8 pb-8 pt-2" data-id="element-1498">
                        <div className="pl-16" data-id="element-1499">
                          <p className="text-gray-600 text-lg leading-relaxed mb-8" data-id="element-1500">
                            {statement.content}
                          </p>
                          {statement.scripture && <div className="bg-[#F5F7FA] rounded-xl p-5 flex items-start gap-4 border-l-4 border-[#fc0719]" data-id="element-1501">
                              <BookOpen className="w-5 h-5 text-[#fc0719] shrink-0 mt-1" data-id="element-1502" />
                              <div data-id="element-1503">
                                <span className="block text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] mb-1" data-id="element-1504">
                                  Scripture Reference
                                </span>
                                <p className="text-[#0A1F44] font-bold italic" data-id="element-1505">
                                  {statement.scripture}
                                </p>
                              </div>
                            </div>}
                        </div>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-24 bg-white" data-id="element-1506">
        <div className="container-custom" data-id="element-1507">
          <div className="bg-gradient-to-br from-[#0A1F44] to-[#163A70] rounded-[2rem] p-12 md:p-20 text-center relative overflow-hidden shadow-2xl" data-id="element-1508">
            <div className="absolute top-0 right-0 w-96 h-96 bg-[#fc0719]/10 rounded-full blur-[100px] -mr-48 -mt-48" data-id="element-1509" />
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-500/10 rounded-full blur-[100px] -ml-48 -mb-48" data-id="element-1510" />

            <div className="relative z-10 max-w-3xl mx-auto" data-id="element-1511">
              <CheckCircle2 className="w-16 h-16 text-[#fc0719] mx-auto mb-8" data-id="element-1512" />
              <h2 className="text-3xl md:text-5xl font-serif font-bold text-white mb-8" data-id="element-1513">
                Join Us in Our Journey of Faith
              </h2>
              <p className="text-xl text-white/80 mb-12 leading-relaxed" data-id="element-1514">
                Whether you're just starting your spiritual journey or looking
                for a place to call home, we invite you to experience the
                life-transforming power of God's Word with us.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-6" data-id="element-1515">
                <motion.a href="/visit" whileHover={{
                scale: 1.05
              }} whileTap={{
                scale: 0.95
              }} className="w-full sm:w-auto bg-[#fc0719] hover:bg-[#d4061a] text-white font-bold tracking-widest uppercase px-10 py-5 rounded-xl shadow-xl shadow-red-900/20 transition-all" data-id="element-1516">
                  Plan Your Visit
                </motion.a>
                <motion.a href="/contact" whileHover={{
                scale: 1.05
              }} whileTap={{
                scale: 0.95
              }} className="w-full sm:w-auto bg-white/10 hover:bg-white/20 text-white font-bold tracking-widest uppercase px-10 py-5 rounded-xl border border-white/20 backdrop-blur-sm transition-all" data-id="element-1517">
                  Contact Us
                </motion.a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>;
}